import {
  Button,
  Cell,
  DropdownDivider,
  DropdownItem,
  Grid,
  HFlow,
  Icon,
  Tag,
  Text,
  Theme,
  Tooltip,
  useStyles,
  VFlow,
} from 'bold-ui'
import { orange } from 'bold-ui/lib/styles/colors'
import { useAlert } from 'components/alert'
import CheckPermission from 'components/auth/CheckPermission'
import { useAcessoLotacao } from 'components/auth/useAcessoLotacao'
import useSession from 'components/auth/useSession'
import { DateTime } from 'components/date'
import { DropdownButton } from 'components/dropdown'
import { useErrorHandler } from 'components/error'
import { toEsusParameters } from 'components/esus'
import { confirm } from 'components/modals/confirm'
import { ATENDIMENTO_NOVO_ENABLED, ATENDIMENTO_ODONTOLOGICO_ENABLED, ATENDIMENTO_VACINACAO_ENABLED } from 'config/flags'
import {
  useAtenderMutation,
  useCancelarAusenciaMutation,
  useExcluirAtendimentoMutation,
  useInformarAusenciaMutation,
  useRealizarAtendimentoVacinacaoMutation,
  useRealizarEscutaInicialMutation,
  useSalvarJustificativaAutomaticaVisualizarEscutaInicialMutation,
  useSalvarJustificativaAutomaticaVisualizarProntuarioMutation,
} from 'graphql/hooks.generated'
import { StatusAtendimento } from 'graphql/types.generated'
import { useRouter } from 'hooks/useRouter'
import moment from 'moment'
import React, { CSSProperties } from 'react'
import Permissions from 'types/Permissions'
import { isCboCirurgiaoDentista } from 'util/atendimento/cboMedicoOuCirurgiaoDentista'
import { humanizeAge } from 'util/date/humanize-age'
import { AtendimentoModel } from 'view/atendimentos/list/ListaAtendimentoView'

import { AtendimentoTags } from './AtendimentoTags'
import { useListaAtendimentoStyles } from './useListaAtendimentoStyles'

export interface ListaAtendimentoItemProps {
  atend: AtendimentoModel
}

export function ListaAtendimentoItem(props: ListaAtendimentoItemProps) {
  const { atend } = props
  const { classes, css } = useStyles(createStyles)
  const statusMap = useListaAtendimentoStyles()
  const statusAtendimento = statusMap.get(atend.statusAtendimento)
  const { history, match } = useRouter()
  const alert = useAlert()
  const [atender] = useAtenderMutation()
  const [realizarEscutaInicial] = useRealizarEscutaInicialMutation()
  const [vacinacao] = useRealizarAtendimentoVacinacaoMutation()
  const handleRejection = useErrorHandler()
  const [salvarJustificativaVisualizarProntuario] = useSalvarJustificativaAutomaticaVisualizarProntuarioMutation()
  const [salvarJustificativaVisualizarEscutaInicial] = useSalvarJustificativaAutomaticaVisualizarEscutaInicialMutation()
  const [informarAusencia] = useInformarAusenciaMutation()
  const [cancelarAusencia] = useCancelarAusenciaMutation()
  const [excluir] = useExcluirAtendimentoMutation()
  const { id: idAcesso } = useAcessoLotacao()
  const { data: sessao } = useSession()
  const isDentista = isCboCirurgiaoDentista(sessao.acesso.__typename === 'Lotacao' && sessao.acesso.cbo.cbo2002)

  const onAtenderClick = () => {
    return atender({ variables: { atendimento: atend.id } })
      .then((res) => {
        if (ATENDIMENTO_NOVO_ENABLED && (!isDentista || ATENDIMENTO_ODONTOLOGICO_ENABLED)) {
          history.push(`/lista-atendimento/next/${atend.id}`)
        } else {
          history.push(
            `/lista-atendimento/atendimento/${atend.id}?${toEsusParameters({
              atendimentoId: res.data.atender.id,
              cidadaoId: res.data.atender.cidadao.id,
              atendProfId: res.data.atender.atendimentoProfissional.id,
              pecBackLink: btoa(match.url),
            })}`
          )
        }

        return res
      })
      .catch(handleRejection)
  }

  const onAtenderVacinacao = () => {
    return vacinacao({ variables: { atendimento: atend.id } })
      .then((res) => {
        if (ATENDIMENTO_VACINACAO_ENABLED) {
          history.push(`/lista-atendimento/next/${atend.id}`)
        } else {
          history.push(
            `/lista-atendimento/atendimentoVacinacao/${atend.id}?${toEsusParameters({
              atendimentoId: res.data.realizarAtendimentoVacinacao.id,
              cidadaoId: res.data.realizarAtendimentoVacinacao.cidadao.id,
              atendProfId: res.data.realizarAtendimentoVacinacao.atendimentoProfissional.id,
              pecBackLink: match.url,
            })}`
          )
        }
        return res
      })
      .catch(handleRejection)
  }

  const onEscutaInicialClick = () => {
    return realizarEscutaInicial({ variables: { atendimento: atend.id } })
      .then((res) => {
        if (ATENDIMENTO_NOVO_ENABLED) {
          history.push(`/lista-atendimento/next/${atend.id}`)
        } else {
          history.push(
            `/lista-atendimento/escuta/${atend.id}?${toEsusParameters({
              atendimentoId: res.data.realizarEscutaInicial.id,
              cidadaoId: res.data.realizarEscutaInicial.cidadao.id,
              atendProfId: res.data.realizarEscutaInicial.atendimentoProfissional.id,
              pecBackLink: btoa(match.url),
            })}`
          )
        }
        return res
      })
      .catch(handleRejection)
  }

  const onVisualizarProntuarioClick = () => {
    return salvarJustificativaVisualizarProntuario({ variables: { idCidadao: atend.cidadao.id } })
      .then(() => {
        history.push(
          `/lista-atendimento/atendimento/visualizarProntuario/${atend.id}?${toEsusParameters({
            atendimentoId: atend.id,
            cidadaoId: atend.cidadao.id,
            isVisualizarProntuario: true,
            pecBackLink: btoa(match.url),
          })}`
        )
      })
      .catch(handleRejection)
  }

  const onInformarAusencia = () => {
    return informarAusencia({ variables: { atendimento: atend.id } })
      .then((res) => {
        alert('success', 'Ausência do cidadão informada.')
      })
      .catch(handleRejection)
  }

  const onCancelarAusencia = () => {
    return cancelarAusencia({ variables: { atendimento: atend.id } })
      .then((res) => {
        alert('success', 'Cidadão foi reinserido com sucesso.')
      })
      .catch(handleRejection)
  }

  const onGerarDeclaracao = () => {
    return history.push(`/lista-atendimento/declaracaoComparecimento/${atend.id}?callbackUrl=lista-atendimento`)
  }

  const onVisualizarAtendimentosDia = () => {
    return history.push(`/lista-atendimento/atendimentoDoDia/${atend.id}`)
  }

  const onVisualizarEscutaInicialClick = () => {
    return salvarJustificativaVisualizarEscutaInicial({ variables: { idCidadao: atend.cidadao.id } })
      .then(() => {
        history.push(
          `/lista-atendimento/escuta/${atend.id}?${toEsusParameters({
            atendimentoId: atend.id,
            cidadaoId: atend.cidadao.id,
            pecBackLink: btoa(match.url),
            readOnly: true,
          })}`
        )
      })
      .catch(handleRejection)
  }

  const onEditarClick = () => {
    return history.push(`/lista-atendimento/editar/${atend.id}`)
  }

  const onExcluirClick = () => {
    confirm({
      title: 'Deseja realmente excluir o registro de atendimento?',
      type: 'danger',
      confirmLabel: 'Excluir',
      onConfirm: () => {
        excluir({ variables: { atendimento: atend.id } })
          .then((res) => {
            alert('success', 'Registro excluído com sucesso.')
          })
          .catch(handleRejection)
      },
    })()
  }

  return (
    <div className={css(classes.container, statusAtendimento.styleBorder)}>
      <div className={css(statusAtendimento.styleBox, classes.boxIcon)} />
      <Grid style={classes.content}>
        <Cell size={5} style={classes.cell}>
          {!atend.finalizado && (
            <div>
              <Tag icon='exclamationTriangleFilled' style={classes.naoFinalizadoTag}>
                {`Atendimento não finalizado - ${moment(atend.iniciadoEm).format('DD/MM/YYYY')}`}
              </Tag>
            </div>
          )}
          <span className={classes.nomeCidadao}>{`${atend.cidadao.nomeSocial || atend.cidadao.nome} `}</span>
          <span className={classes.idadeCidadao}>{`| ${humanizeAge(atend.cidadao.dataNascimento)}`}</span>

          <div className={css(classes.cidadaoStatusLine)}>
            <b>
              <DateTime
                value={atend.ultimaAlteracaoStatusEm}
                format={
                  atend.statusAtendimento === StatusAtendimento.NAO_AGUARDOU ||
                  atend.statusAtendimento === StatusAtendimento.ATENDIMENTO_REALIZADO
                    ? 'DD/MM/YYYY [-] HH:mm'
                    : 'HH:mm'
                }
              />
            </b>
            {' | '}
            {statusAtendimento.descricao}
          </div>
        </Cell>
        <Cell size={3} style={classes.cell}>
          <AtendimentoTags
            tiposServico={atend.tiposServico}
            classificacaoRisco={atend.classificacaoRisco}
            horaAgendamento={atend.agendado?.horarioInicial}
          />
        </Cell>
        <Cell size={4} alignSelf='center' style={classes.cell}>
          <HFlow alignItems='center' style={{ display: 'flex' }}>
            {atend.lotacao && (
              <VFlow vSpacing={0}>
                <Text fontWeight='bold'>{atend.lotacao.profissional.nome}</Text>
              </VFlow>
            )}
            {!atend.lotacao && atend.equipe && (
              <VFlow vSpacing={0}>
                <Text fontWeight='bold'> {atend.equipe.nome} </Text>
                <Text>
                  <b>Área </b> {atend.equipe.area}
                </Text>
              </VFlow>
            )}
            <HFlow hSpacing={0.25} style={{ marginRight: '0.5rem', marginLeft: 'auto' }}>
              {atend.actions.escutaInicial ? (
                <CheckPermission
                  permission={Permissions.visualizarListaDeAtendimento.visualizarEscutaInicial.realizarEscutaInicial}
                >
                  <Tooltip text={atend.actions.escutaInicial?.hint}>
                    <Button
                      size='small'
                      kind='normal'
                      skin='ghost'
                      onClick={onEscutaInicialClick}
                      disabled={!atend.actions.escutaInicial?.enabled}
                    >
                      <Icon icon='nurseOutline' />
                    </Button>
                  </Tooltip>
                </CheckPermission>
              ) : (
                <CheckPermission permission={Permissions.visualizarListaDeAtendimento.visualizarEscutaInicial}>
                  <Tooltip text={atend.actions.visualizarEscutaInicial?.hint}>
                    <Button
                      size='small'
                      kind='normal'
                      skin='ghost'
                      onClick={onVisualizarEscutaInicialClick}
                      disabled={!atend.actions.visualizarEscutaInicial?.enabled}
                    >
                      <Icon icon='nurseVisualizationOutline' />
                    </Button>
                  </Tooltip>
                </CheckPermission>
              )}

              {atend.actions.vacinacao && (
                <Tooltip text={atend.actions.vacinacao.hint}>
                  <Button
                    size='small'
                    kind='normal'
                    skin='ghost'
                    onClick={onAtenderVacinacao}
                    disabled={!atend.actions.vacinacao.enabled}
                  >
                    <Icon icon='needleOutline' />
                  </Button>
                </Tooltip>
              )}

              {!atend.actions.vacinacao && atend.actions.atender && (
                <Tooltip text={atend.actions.atender.hint}>
                  <Button
                    size='small'
                    kind='normal'
                    skin='ghost'
                    onClick={onAtenderClick}
                    disabled={!atend.actions.atender.enabled}
                  >
                    <Icon icon='multipleUsersOutline' />
                  </Button>
                </Tooltip>
              )}

              <Tooltip text='Mais opções'>
                <DropdownButton icon='dots'>
                  {atend.actions.vacinacao && atend.actions.atender && (
                    <Tooltip text={!atend.actions.atender.enabled && atend.actions.atender?.hint}>
                      <DropdownItem onClick={onAtenderClick} disabled={!atend.actions.atender.enabled}>
                        {atend.statusAtendimento === 'EM_ATENDIMENTO' &&
                        !atend.actions.vacinacao.enabled &&
                        atend.lotacao.id === idAcesso
                          ? 'Continuar atendimento'
                          : 'Realizar atendimento'}
                      </DropdownItem>
                    </Tooltip>
                  )}

                  <CheckPermission permission={Permissions.visualizarListaDeAtendimento.informarQueCidadaoNaoAguardou}>
                    {atend.actions.cancelarAusencia ? (
                      <Tooltip text={atend.actions.cancelarAusencia?.hint}>
                        <DropdownItem disabled={!atend.actions.cancelarAusencia?.enabled} onClick={onCancelarAusencia}>
                          Cidadão retornou
                        </DropdownItem>
                      </Tooltip>
                    ) : (
                      <Tooltip text={atend.actions.informarAusencia?.hint}>
                        <DropdownItem disabled={!atend.actions.informarAusencia?.enabled} onClick={onInformarAusencia}>
                          Cidadão não aguardou
                        </DropdownItem>
                      </Tooltip>
                    )}
                  </CheckPermission>

                  {atend.actions.declaracaoComparecimento && (
                    <DropdownItem
                      disabled={!atend.actions.declaracaoComparecimento.enabled}
                      onClick={onGerarDeclaracao}
                    >
                      Gerar declaração de comparecimento
                    </DropdownItem>
                  )}

                  {atend.actions.visualizarProntuario && (
                    <DropdownItem
                      disabled={!atend.actions.visualizarProntuario.enabled}
                      onClick={onVisualizarProntuarioClick}
                    >
                      Visualizar prontuário
                    </DropdownItem>
                  )}

                  {atend.actions.atendimentosDia && (
                    <Tooltip text={atend.actions.atendimentosDia?.hint}>
                      <DropdownItem
                        disabled={!atend.actions.atendimentosDia.enabled}
                        onClick={onVisualizarAtendimentosDia}
                      >
                        Visualizar atendimentos do dia
                      </DropdownItem>
                    </Tooltip>
                  )}

                  {atend.actions.editar && (
                    <Tooltip text={atend.actions.editar?.hint}>
                      <DropdownItem disabled={!atend.actions.editar.enabled} onClick={onEditarClick}>
                        Editar
                      </DropdownItem>
                    </Tooltip>
                  )}

                  {atend.actions.excluir && (
                    <>
                      <DropdownDivider />

                      <Tooltip text={atend.actions.excluir?.hint}>
                        <DropdownItem type='danger' disabled={!atend.actions.excluir.enabled} onClick={onExcluirClick}>
                          <HFlow alignItems='center' hSpacing={0.5}>
                            <Icon icon='trashOutline' />
                            Excluir
                          </HFlow>
                        </DropdownItem>
                      </Tooltip>
                    </>
                  )}
                </DropdownButton>
              </Tooltip>
            </HFlow>
          </HFlow>
        </Cell>
      </Grid>
    </div>
  )
}

const createStyles = (theme: Theme) => ({
  container: {
    border: `1px solid ${theme.pallete.divider}`,
    minHeight: '3rem',
    display: 'flex',
    backgroundColor: theme.pallete.surface.main,
    borderRadius: 2,
  } as CSSProperties,

  content: {
    flex: 1,
    padding: '0.5rem 0',
  } as CSSProperties,

  cell: {
    paddingTop: '0.25rem',
    paddingBottom: '0.25rem',
  } as CSSProperties,

  nomeCidadao: {
    fontSize: '0.875rem',
    fontWeight: 'bold',
    lineHeight: '1.5',
    fontFamily: theme.typography.fontFamily,
  } as CSSProperties,

  idadeCidadao: {
    fontSize: '13px',
    lineHeight: '1.5',
    fontFamily: theme.typography.fontFamily,
    color: theme.pallete.gray.c20,
  } as CSSProperties,

  boxIcon: {
    width: '0.5rem',
    margin: '0rem 1rem 0rem 0rem',
  } as CSSProperties,

  cidadaoStatusLine: {
    lineHeight: '1.25rem',
  } as CSSProperties,

  profissionalNome: {
    fontWeight: 'bold',
  } as CSSProperties,

  naoFinalizadoTag: {
    backgroundColor: orange.c90,
    color: orange.c40,
  },
})
